import Input from '../common/Input';
import Button from '../common/Button';
import GraphErrorDetails from './GraphErrorDetails';

/**
 * Microsoft Graph Settings Component
 * Form for configuring Microsoft Graph API (Office 365) email settings
 */
export default function GraphSettings({ settings, onChange, onTestConnection, onTestEmail, connectionStatus, connectionError }) {
  const handleChange = (field, value) => {
    onChange({ ...settings, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Microsoft Graph API Configuration</h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure Microsoft Graph API (Office 365) for sending emails via Azure AD
        </p>
        {connectionStatus && (
          <div className="mt-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              connectionStatus === 'connected'
                ? 'bg-green-100 text-green-800'
                : connectionStatus === 'testing'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {connectionStatus === 'connected' && '✓ Connected'}
              {connectionStatus === 'testing' && '⏳ Testing...'}
              {connectionStatus === 'failed' && '✗ Connection Failed'}
            </span>
          </div>
        )}
      </div>

      {connectionError && (
        <GraphErrorDetails error={connectionError} />
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">Azure AD App Registration Required</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>To use Microsoft Graph API, you need to:</p>
              <ol className="list-decimal list-inside mt-1 space-y-1">
                <li>Register an app in Azure AD (Azure Portal → App Registrations)</li>
                <li>Grant <strong>Mail.Send</strong> application permission</li>
                <li>Create a client secret and note the values</li>
                <li>Admin consent must be granted for the Mail.Send permission</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Tenant ID"
          value={settings.graph_tenant_id || ''}
          onChange={(e) => handleChange('graph_tenant_id', e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          required
          helpText="Your Azure AD tenant ID (found in Azure Portal → Azure Active Directory)"
        />

        <Input
          label="Client ID (Application ID)"
          value={settings.graph_client_id || ''}
          onChange={(e) => handleChange('graph_client_id', e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
          required
          helpText="Application (client) ID from your app registration"
        />

        <Input
          label="Client Secret"
          type="password"
          value={settings.graph_client_secret || ''}
          onChange={(e) => handleChange('graph_client_secret', e.target.value)}
          placeholder="Enter client secret"
          required
          helpText="Client secret value (not the secret ID)"
        />

        <Input
          label="User Email (Send From)"
          type="email"
          value={settings.graph_user_email || ''}
          onChange={(e) => handleChange('graph_user_email', e.target.value)}
          placeholder="noreply@yourdomain.com"
          required
          helpText="The mailbox to send emails from (must have a valid M365 license)"
        />

        <Input
          label="Sender Name"
          value={settings.graph_from_name || ''}
          onChange={(e) => handleChange('graph_from_name', e.target.value)}
          placeholder="Contract Management System"
          helpText="Display name for sent emails"
        />

        <Input
          label="Sender Email"
          type="email"
          value={settings.graph_from_email || ''}
          onChange={(e) => handleChange('graph_from_email', e.target.value)}
          placeholder="noreply@yourdomain.com"
          helpText="From email address (usually same as User Email)"
        />
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={onTestConnection}>
          Test Connection
        </Button>
        <Button variant="secondary" onClick={onTestEmail}>
          Send Test Email
        </Button>
      </div>
    </div>
  );
}